/** Per-member DSR action buttons — Phase 2B-ui.
 *
 * Sits inside the MemberList row. Two actions:
 *   - Export → POST /v1/dsr/users/{id}/export, downloads JSON bundle.
 *   - Erase  → opens DsrEraseModal (typed-confirmation).
 *
 * Both gated on `dsr.execute` at the component layer via PermissionGate
 * — server-side HasOrgPermission is the authoritative gate.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PermissionGate } from "@/components/PermissionGate";

import {
  downloadExportBundle,
  exportUser,
  fetchExportStatus,
  startExportArchive,
} from "./api";
import { DsrEraseModal } from "./DsrEraseModal";
import type { OrgMember } from "./types";


type Props = { member: OrgMember };


export function MemberDsrActions({ member }: Props) {
  const { t } = useTranslation();
  const [eraseOpen, setEraseOpen] = useState(false);
  const [archiveJobId, setArchiveJobId] = useState<string | null>(null);

  const exportM = useMutation({
    mutationFn: () => exportUser(member.user_id),
    onSuccess: (bundle) => {
      downloadExportBundle({ bundle });
    },
  });

  const archiveStart = useMutation({
    mutationFn: () => startExportArchive(member.user_id),
    onSuccess: (r) => setArchiveJobId(r.job_id),
  });

  const archiveStatus = useQuery({
    queryKey: ["dsr-export-status", archiveJobId],
    queryFn: () => fetchExportStatus(archiveJobId!),
    enabled: !!archiveJobId,
    refetchInterval: (q) => {
      const s = q.state.data?.state;
      return s === "ready" || s === "failed" ? false : 2000;
    },
  });

  return (
    <PermissionGate perm="dsr.execute">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => exportM.mutate()}
          disabled={exportM.isPending}
          title={t("ui.memberDsrActions.exportTitle")}
        >
          {exportM.isPending ? t("ui.memberDsrActions.exporting") : t("ui.memberDsrActions.export")}
        </button>
        <ArchiveButton
          pending={archiveStart.isPending}
          status={archiveStatus.data}
          onStart={() => archiveStart.mutate()}
        />
        <button
          type="button"
          className="btn-ghost text-xs text-cvp-danger hover:bg-cvp-danger/10"
          onClick={() => setEraseOpen(true)}
          disabled={member.erased}
          title={
            member.erased
              ? t("ui.memberDsrActions.alreadyErased")
              : t("ui.memberDsrActions.eraseTitle")
          }
        >
          {member.erased ? t("ui.memberDsrActions.erased") : t("ui.memberDsrActions.erase")}
        </button>
        {eraseOpen && (
          <DsrEraseModal member={member} onClose={() => setEraseOpen(false)} />
        )}
      </div>
    </PermissionGate>
  );
}


function ArchiveButton({
  pending,
  status,
  onStart,
}: {
  pending: boolean;
  status?: { state: string; download_url?: string; error_msg?: string };
  onStart: () => void;
}) {
  const { t } = useTranslation();
  if (status?.state === "ready" && status.download_url) {
    return (
      <a
        href={status.download_url}
        target="_blank"
        rel="noreferrer noopener"
        className="btn-ghost text-xs text-cvp-ok"
        title={t("ui.memberDsrActions.zipReadyTitle")}
      >
        {t("ui.memberDsrActions.downloadZip")}
      </a>
    );
  }
  if (status?.state === "failed") {
    return (
      <button
        type="button"
        className="btn-ghost text-xs text-cvp-danger"
        title={status.error_msg ?? t("ui.memberDsrActions.archiveFailed")}
        onClick={onStart}
      >
        {t("ui.memberDsrActions.retryArchive")}
      </button>
    );
  }
  if (status && (status.state === "pending" || status.state === "assembling")) {
    return (
      <span className="px-2 text-xs text-cvp-warn">
        {t("ui.memberDsrActions.assembling")}
      </span>
    );
  }
  return (
    <button
      type="button"
      className="btn-ghost text-xs"
      onClick={onStart}
      disabled={pending}
      title={t("ui.memberDsrActions.archiveTitle")}
    >
      {pending ? t("ui.memberDsrActions.starting") : t("ui.memberDsrActions.archive")}
    </button>
  );
}